// Movement Resources Content - Structured for clean display

export const movementContent = {
  'holistic-training-nutshell': {
    id: 'holistic-training-nutshell',
    title: 'Holistic/Restorative Personal Training in a Nutshell',
    subtitle: 'Why our sessions are only the beginning',
    image: 'https://customer-assets.emergentagent.com/job_06c44a8a-5d97-48ba-9afb-b8fb64162499/artifacts/ppnujetj_AHHW%20Kbell3.png',
    introduction: "When most people come to personal training, they're expecting a workout. And yes — we move, we strengthen, we build. But if you've worked with me, you've probably already felt… it's not just that. Because your body isn't just muscles. It's a full system — physical, mental, emotional, and energetic — all constantly communicating with each other. So the work we do together isn't just about getting stronger. It's about helping your body return to how it's designed to function.",
    keyInsight: "Our sessions are the starting point. What you begin to notice, shift, and practice in your daily life… that's where the real change happens.",
    sections: [
      {
        title: "Corrective Exercise",
        subtitle: "Rebuilding Your Foundations",
        icon: "logo",
        content: "In my training — particularly through CHEK (Corrective Holistic Exercise Kinesiology) — I've learned to look at the body as a system that's constantly adapting, compensating, and communicating. So instead of just adding more exercises, we focus on restoring the foundations of how your body moves. At the core, your body is designed around a few key movement patterns:",
        items: [
          { term: "Squat (deep)", definition: "The primal resting and picking-up pattern." },
          { term: "Lunge", definition: "The walking, stepping, and stair-climbing pattern." },
          { term: "Push", definition: "From the floor, overhead, or in front of you." },
          { term: "Pull", definition: "Drawing toward you, opening the chest and back." },
          { term: "Twist (rotate)", definition: "Spinal rotation — essential for real-life movement." },
          { term: "Bend (hinge)", definition: "Hinging at the hips — the foundation of picking anything up." }
        ],
        subsections: [
          { type: "When patterns work", tip: "Your body feels supported, strong, and efficient." },
          { type: "When they don't", tip: "Your body starts to compensate — and that's often where pain, tension, or those 'I don't know why this hurts' moments begin." }
        ]
      },
      {
        title: "Our Work Together",
        subtitle: "Restoring the whole system",
        icon: "logo",
        content: "Our work focuses on restoring the foundations of how your body moves as an integrated whole:",
        items: [
          { term: "Posture and alignment", definition: "Coming home to your natural axis." },
          { term: "Core stability", definition: "Deep support from the inside out." },
          { term: "Mobility and flexibility", definition: "Moving freely through your full range." },
          { term: "Breathing mechanics", definition: "Re-teaching the body to breathe efficiently." },
          { term: "Functional strength", definition: "Strength that translates into daily life." },
          { term: "Joint mechanics", definition: "Function, efficiency, and proper sequencing." }
        ],
        subsections: [
          { type: "Why we slow down", tip: "Because when a joint isn't doing its job… something else quietly takes over. And over time, that 'something else' gets overworked. We're not just training your body — we're re-educating how it moves and functions as a whole system." }
        ]
      },
      {
        title: "Lifestyle & Daily Habits",
        subtitle: "What happens outside the session",
        icon: "logo",
        content: "One of the biggest things I've learned — through both my education and working with real people — is this: What you do outside of our sessions matters just as much (if not more) than what we do inside them. Assuming you sleep roughly 8 hrs/night, there are 112 waking hours in a week. We spend 1–3 hrs together. Your body is always responding to:",
        items: [
          { term: "Sleep", definition: "Or the lack of it." },
          { term: "Stress levels", definition: "What your nervous system is carrying day to day." },
          { term: "Hydration", definition: "Or the lack of it." },
          { term: "Nutrition", definition: "Or the lack of it." },
          { term: "Movement & rest", definition: "How much you sit, move, or rest." },
          { term: "Environment & rhythm", definition: "Your daily surroundings and pace of life." }
        ],
        subsections: [
          { type: "A gentle note", tip: "If something isn't shifting in your body, we gently start to look here. Not to overwhelm you — but to support you in making small, meaningful changes that your body can actually integrate." }
        ]
      },
      {
        title: "Integration",
        subtitle: "Connecting the dots",
        icon: "logo",
        content: "This is where it all comes together. Because your body doesn't separate things the way we often do.",
        items: [
          { term: "Tight hips", definition: "Might be connected to how you breathe." },
          { term: "Shoulders", definition: "Might reflect your stress levels." },
          { term: "Energy", definition: "Might be influenced by how well you're recovering." },
          { term: "Knee pain", definition: "Might be about fear of stepping into the future." }
        ],
        subsections: [
          { type: "My role", tip: "Part of my role is to help you start to see those connections. Not so you feel like something is 'wrong'… but so you can begin to understand your body in a new way." }
        ]
      }
    ],
    practicalGuidance: {
      title: "The Real Work",
      icon: "logo",
      steps: [
        { title: "Notice how you're sitting", description: "Your posture throughout the day is shaping your body far more than one hour of training." },
        { title: "Catch your breath patterns", description: "Breath is the quietest, most powerful regulator you have. Check in with it often." },
        { title: "Move a little differently", description: "Small adjustments — how you stand, walk, lift, reach — add up to big changes over time." },
        { title: "Give your body what it's been asking for", description: "Rest, water, nourishment, stillness, connection. Listen to the ask." }
      ]
    },
    closingThought: "This isn't about perfection.\nAnd it's not about doing everything at once.\n\nIt's about becoming more aware, more connected, and more supportive of your body over time.\n\nGetting stronger is the easy part.\nShifting the patterns and blueprints your body has learned to rely on —\nphysical, habitual, mental, emotional and otherwise —\nthat's where the real work begins.\n\nYou don't need to fix everything.\nIt's better to start listening and having a conversation with your body.",
    closingQuote: "Our sessions are where we explore, assess, and guide. But the real work happens in the quiet moments in between."
  },

  'tending-to-injury': {
    id: 'tending-to-injury',
    title: 'Tending to Your Injury',
    subtitle: 'A Holistic Guide to Healing',
    image: 'https://images.unsplash.com/photo-1571019613454-1cb2f99b2d8b?w=800',
    introduction: "Recovery varies greatly from person to person. The nature and severity of your injury—whether acute or chronic, an accident or surgery—will shape not only healing time but also the emotions behind it. Our bodies are designed to heal, but we often become frustrated with the time required.",
    keyInsight: "Healing is the absence of the irritant and the addition of 'healing' elements. We only need to consider: is it facilitating healing or impeding healing?",
    sections: [
      {
        title: "R.I.P. RICE",
        icon: "logo",
        content: "Let's put RICE (Rest, Ice, Compress, Elevate) away—it's old school. Ice slows healing. Better approach: alternate cold and hot to facilitate blood flow. Welcome to the new age of PEACE and LOVE."
      },
      {
        title: "PEACE",
        subtitle: "For the immediate, acute phase (first 1-3 days)",
        icon: "logo",
        items: [
          { term: "P = Protect", definition: "Limit damaging movement, but avoid prolonged rest—use pain as your guide" },
          { term: "E = Elevate", definition: "Raise the injured area above heart level to help fluid drainage" },
          { term: "A = Avoid anti-inflammatories", definition: "Avoid NSAIDs and ice—dampening inflammation can hinder natural healing" },
          { term: "C = Compress", definition: "Gentle bandaging helps reduce swelling while allowing movement" },
          { term: "E = Educate", definition: "Understand healing, avoid over-reliance on passive treatments" }
        ]
      },
      {
        title: "LOVE",
        subtitle: "For ongoing recovery and rehabilitation",
        icon: "logo",
        items: [
          { term: "L = Load", definition: "Gradually add stress back to the tissue—guided by symptom tolerance" },
          { term: "O = Optimism", definition: "A positive mindset supports better recovery outcomes" },
          { term: "V = Vascularization", definition: "Gentle cardio (walking, biking, swimming) enhances blood flow" },
          { term: "E = Exercise", definition: "Active movement and targeted strengthening aid long-term recovery" }
        ]
      },
      {
        title: "Rest",
        icon: "logo",
        content: "This seems too simple—but it truly isn't. Injuries heal on the body's timeline, not our ego's. An animal lies down and rests until it can get up again. Most of us try to bypass rest. Know you need more rest than usual!",
        subsections: [
          { type: "Acute injuries", tip: "Recent, severe, highly inflamed—need lots of rest. Good time for anti-inflammatories if needed." },
          { type: "Chronic injuries", tip: "Long-term, less inflamed—need the right balance of rest and movement. Finding the best exercises may require professional guidance." }
        ]
      }
    ],
    practicalTips: [
      {
        title: "Topicals",
        icon: "logo",
        tips: [
          "Arnica — Homeopathic, you cannot overdose! Use often for healing, inflammation, bruising",
          "Magnesium — Natural muscle relaxant",
          "Voltaren or other anti-inflammatory rubs — Time and place for these",
          "Tiger balm, wintergreen — Great for pain management"
        ],
        note: "Check with your pharmacist if on any medications for interactions!"
      },
      {
        title: "Sleep",
        icon: "logo",
        tips: [
          "Aim for 10pm bedtime as often as possible",
          "Healing hormones are only active from 11pm-2am",
          "Consider Magnesium Citrate or Glycinate as tea before bed",
          "Topical magnesium can also help"
        ]
      },
      {
        title: "Hydration",
        icon: "logo",
        tips: [
          "2-3 litres of water per day minimum",
          "Coconut water 1-3 cups daily for healthy electrolytes",
          "Small pinch of pink salt for minerals (should not taste it)"
        ]
      },
      {
        title: "Nutrition",
        icon: "logo",
        tips: [
          "Fat: 1-2 Tbsp coconut oil daily (supports hormones and tissue)",
          "Supplements: Vit C, Silica, Arnica, MSM, CoQ10, Turmeric, Collagen",
          "Bone broths: Contain healing minerals, collagen, protein (cook 24-72 hours)",
          "Avoid sugar: Robs your immune system of healing capacity"
        ]
      },
      {
        title: "Compresses",
        icon: "logo",
        tips: [
          "Ice only in first 24 hours (if significant injury)",
          "Hot moist compresses work well with arnica and/or magnesium",
          "Thermal heat packs (vs chemical) can stay warm 8 hours",
          "Chemical heat like capsicum redirects the brain from pain"
        ]
      },
      {
        title: "Massage",
        icon: "logo",
        tips: [
          "Massage affected area gently (appropriate for injury)",
          "Use oil, arnica, or magnesium spray",
          "Brings blood and immune system to the area"
        ]
      }
    ],
    complementaryHealing: {
      title: "Complementary Healing Methods",
      icon: "logo",
      intro: "Any healing services you can add will be helpful. In every field there are good, great and average practitioners—find one that works for you.",
      regulated: ["Chiropractor", "Physiotherapist", "Osteopath", "Massage Therapist", "Acupuncturist"],
      alternative: ["Chinese Acupuncture", "Thai Massage", "Reflexology", "Reiki", "Craniosacral", "Feldenkrais"]
    },
    mindBody: {
      title: "Mind-Body Connection",
      icon: "logo",
      intro: "Our healing abilities live in the Parasympathetic Nervous System—the 'Rest and Digest' system. Injury and pain launch us into Fight/Flight mode, creating anxiety that's tough to explain.",
      practices: [
        {
          title: "Practice the Love Philosophy",
          description: "Notice how you speak to your injury. With love or loathe? We often call injuries 'stupid' or are impatient. Would you speak to a child or best friend this way? Develop a loving, listening relationship with your body."
        },
        {
          title: "Treat Yourself Like a Newborn",
          description: "We're never angry with a baby for crying. Learn your body's language—it's challenging but necessary."
        },
        {
          title: "RAIN",
          description: "Recognize the pain → Accept it exists → Invite healing → Nurture all. Please know this is not easy."
        },
        {
          title: "The 'Why Me' Phase",
          description: "A healing place where we focus on self. We can get stuck here—or never let ourselves go here at all. Both extremes slow healing."
        }
      ]
    },
    quotes: [
      "The body is a self-healing organism—it's about clearing things out of the way so the body can heal itself.",
      "Healing is a matter of time, but it is sometimes also a matter of opportunity. — Hippocrates",
      "The natural healing force within each of us is the greatest force in getting well. — Hippocrates"
    ],
    books: [
      { title: "Full Catastrophe Living", author: "Jon Kabat-Zinn" },
      { title: "The Impersonal Life", author: "Joseph Benner" },
      { title: "When Pain Is the Doorway", author: "Pema Chödrön" },
      { title: "When the Body Says No", author: "Gabor Maté" },
      { title: "The Body Keeps the Score", author: "Bessel van der Kolk" },
      { title: "You Can Heal Your Life", author: "Louise Hay" }
    ],
    closingThought: "One disease long life, no disease short life. — Chinese Proverb\n\nThe philosophy being: if you learn to care for your body early in your journey, you enjoy a long life. Injury can teach us this valuable aspect of caring for self."
  },
  'bodies-like-water': {
    id: 'bodies-like-water',
    title: 'What if We Treated Our Bodies Like the Ocean?',
    subtitle: 'Restoring Our Relationship with Our Bodies',
    image: 'https://images.unsplash.com/photo-1518837695005-2083093ee35b?w=800',
    downloadUrl: 'https://customer-assets.emergentagent.com/job_hello-app-881/artifacts/inei5ija_What%20if%20we%20Treated%20our%20Bodies%20Like%20Water.pdf',
    introduction: "This is about restoring our relationship with our bodies. The body is made up of massive amounts of water, and water has wisdom to share. Water can teach us how to work with our bodies and the attitude needed for healing, recovery, and restoration—especially when the body breaks down or experiences injury, surgery, or illness.",
    oceanAnalogy: {
      title: "The Ocean Analogy",
      icon: "logo",
      intro: "We need to have respect for water and its conditions on any given day.",
      points: [
        "We don't expect the ocean to be flat calm when it's rough",
        "We don't expect the ocean to conform to our needs",
        "Instead, we tend to our own safety within its conditions",
        "The ocean is not the same each day—it's dynamic",
        "We know this, we expect it, and most importantly... we accept it"
      ],
      insight: "If we could allow this wisdom to flow into our relationship with our bodies, especially during healing, magic happens."
    },
    applyingToBody: {
      title: "Applying This to Our Bodies",
      icon: "logo",
      content: "When we accept the body as it is, in every given moment, magic happens.",
      healing: "When the body is recovering from insult, injury, or surgery, we should accept these conditions not as limitations but as a guidance system.",
      fluidity: "We learn to develop fluidity and flow with what is, working together with our bodies.",
      keyQuote: "Living with the body rather than despite the body."
    },
    dealingWithPain: {
      title: "Dealing with Pain",
      icon: "logo",
      points: [
        "When the body has pain, we are cautious and tend to its conditions appropriately",
        "We make mistakes developing the blueprint for what is enough, too much, when to rest, or when to press forward",
        "We accept each day as it is, knowing it will be new and different"
      ],
      reality: "When we experience setbacks, injury, or pain, we often consciously or unconsciously harbor disdain for our bodies or the pain itself. We get angry or frustrated with perceived limitations, timelines, or progress.",
      rareSkill: "It's rare for many of us to meet our bodies where they are, exactly in the state they are at this very moment."
    },
    bodyShaming: {
      title: "The Language We Use",
      icon: "logo",
      intro: "How many people feel irritated by their injury's lack of progression? How many call their shoulder 'stupid' or their pain 'stupid'?",
      humor: "If I had a dime for every client who called a body part 'stupid,' I'd be rich!",
      dialogue: [
        "\"What?!?! It won't learn the 'get with the program'?\"",
        "\"We got places to be...don't be stupid!\"",
        "\"I have a race this weekend...you can't hurt?\""
      ],
      truth: "If our body were a separate human, we would sound cruel. It's rare to find someone very kind to their bodies and who has the utmost respect—if they do, they have earned and learned it."
    },
    positiveRelationship: {
      title: "A Different Relationship",
      icon: "logo",
      intro: "Some people forge a different relationship with their body. They learn to turn to their body and say:",
      gratitudes: [
        "Thank you. You are my only body, till death do us part.",
        "I will cherish you, love you and respect you for as long as we travel this plane!",
        "Thank you for being my body.",
        "Thank you for holding all this pain. Thank you for holding invisible pain that I could not process cognitively, so you agreed to carry it for me, until I could discover how to release this pain through its proper channels."
      ],
      shift: "People who have such positive relationships often experienced a crisis that required a shift in consciousness—a shift in the relationship between the body, ego, and soul."
    },
    trueHealing: {
      title: "True Healing",
      icon: "logo",
      content: "True healing only comes when we see ourselves as sacred creatures, designed for progressing beyond perceived limitations... IF we can meet ourselves EXACTLY where we are.",
      design: "We are designed to heal and rebound. Sometimes, we face crises that require a significant shift in thinking.",
      mindTricks: "This is due to our minds playing tricks on us and covert messages being deposited when we aren't paying attention."
    },
    practicalGuidance: {
      title: "Practical Guidance",
      icon: "logo",
      steps: [
        { title: "Listen to the Mind", description: "Listen to the endless chatter. Listen for insults and catch, block, reverse them. Ask yourself what would FEEL more helpful." },
        { title: "Hear with Your Heart", description: "Your body isn't a nuisance; it has so much love for you and chose you as its life partner." },
        { title: "Tap into the Soul", description: "Seek ultimate guidance from your soul." },
        { title: "Emerging from Numbness", description: "If coming out of numbness, there's work to be done, and the rewards are on the other side." },
        { title: "Embrace the Relationship", description: "Tread softly and embrace the relationship between self and body." }
      ]
    },
    keyTakeaways: {
      title: "Key Takeaways",
      icon: "logo",
      points: [
        "Embrace Fluidity — Accept your body's changing conditions and be fluid in your approach",
        "Acceptance is Key — Accept your body as it is, in every moment, especially during healing",
        "Reframe Limitations — View conditions and injuries not as limitations but as guidance",
        "Mindful Language — Avoid negative self-talk and calling body parts 'stupid'",
        "Gratitude — Express thanks to your body for its resilience and capacity",
        "Partnership — View your body as a life partner, not a nuisance",
        "Meet Yourself Where You Are — True healing requires meeting yourself exactly in your current state"
      ]
    },
    closingQuote: "Go through it... tread softly and embrace the relationship between self and body.",
    books: [
      { title: "When the Body Says No", author: "Gabor Maté" },
      { title: "You Can Heal Your Life", author: "Louise Hay" }
    ]
  },
  'etheric-body': {
    id: 'etheric-body',
    title: 'What is the Etheric Body?',
    subtitle: 'Understanding Our Luminous Blueprint',
    image: 'https://images.unsplash.com/photo-1506126613408-eca07ce68773?w=800',
    downloadUrl: 'https://customer-assets.emergentagent.com/job_hello-app-881/artifacts/joq4qzjn_What%20is%20The%20Etheric%20Body%20%28Aura%29.pdf',
    introduction: "The etheric body is often described as the first layer of the human aura – a subtle field of energy that surrounds, penetrates, and sustains the physical body. Many spiritual and esoteric traditions see it as the blueprint of our human form, a luminous double that helps shape, nourish, and repair the physical self.",
    natureOfEtheric: {
      title: "The Nature of the Etheric Body",
      icon: "logo",
      intro: "Unlike flesh and bone, the etheric body is said to be composed of finer, more subtle matter.",
      points: [
        "Connects with the physical body through the chakras and energy channels",
        "Distributes life force to the cells, organs, and systems",
        "Considered essential for health, vitality, and overall well-being",
        "Some people with heightened spiritual sensitivity can perceive it as a faint glow around the body"
      ],
      practices: "Many traditions teach that it can be strengthened through practices such as meditation, breathwork, energy healing, crystal work, or conscious movement."
    },
    originsOfWord: {
      title: "Origins of the Word 'Etheric'",
      icon: "logo",
      content: "The term comes from the Greek word 'aither', meaning 'pure sky' or 'upper air.'",
      cosmology: "In ancient cosmology, aither was the fifth element, believed to fill the heavens and act as the medium for light itself.",
      adoption: "Later spiritual systems borrowed this concept to describe the unseen substance that underlies and sustains physical life."
    },
    esotericTraditions: {
      title: "Esoteric Traditions",
      icon: "logo",
      traditions: [
        {
          name: "Theosophical Tradition",
          description: "Developed further in the late 19th century, teaching that all beings are fundamentally spiritual and that subtle bodies provide the bridge between physical life and higher planes of existence."
        },
        {
          name: "Hermetic Order of the Golden Dawn",
          description: "Wove the etheric body into teachings on ceremonial magic and the Qabalah, emphasizing its role in spiritual development and mystical practice."
        },
        {
          name: "Gnosticism",
          description: "Believed the soul was a divine spark temporarily bound in the material world, and that true liberation came through gnosis (direct spiritual knowledge)."
        }
      ],
      insight: "Both perspectives highlight the human capacity to reach beyond material reality through layers of subtle energy."
    },
    energyField: {
      title: "The Etheric Body as an Energy Field",
      icon: "logo",
      intro: "In practical terms, the etheric body is often described as an energetic buffer.",
      function: "It receives and transmits forces from higher dimensions into the nervous, endocrine, and circulatory systems, revitalizing the cells of the body.",
      benefits: "When well-aligned, it supports mental clarity, physical health, emotional balance, and spiritual growth."
    },
    livingBlueprint: {
      title: "A Living Blueprint",
      icon: "logo",
      content: "Ultimately, the etheric body is seen as a living template – a subtle energy field that both mirrors and informs the physical body.",
      harmony: "In harmony, it not only sustains our health but also awakens us to the deeper layers of our being."
    },
    auraLayers: {
      title: "The Clear Human Aura",
      icon: "logo",
      intro: "The human energy field consists of multiple layers:",
      layers: [
        { name: "Soul Star", description: "Receives incoming universal energy" },
        { name: "Crown Chakra", description: "Radiates soul star energy" },
        { name: "Etheric Body", description: "First layer, sustains physical form" },
        { name: "Emotional Body", description: "Holds emotional energy" },
        { name: "Mental Body", description: "Contains thought patterns" },
        { name: "Astral Body", description: "Bridge to higher dimensions" },
        { name: "Electromagnetic Field", description: "Surrounds and protects the first four spiritual bodies" },
        { name: "Earth Star", description: "Receives incoming earth energy" }
      ]
    },
    constrictionExpansion: {
      title: "Expansion & The Inner Environment",
      icon: "logo",
      intro: "Our etheric body responds to our inner state. When we hold contracted beliefs, our energy field contracts too.",
      contractedStances: [
        "I'm not enough yet",
        "I need to be different to be okay",
        "Something is wrong with me",
        "I can't trust myself"
      ],
      impact: "When we don't like ourselves, even slightly, life tightens. The nervous system senses threat. The body contracts. The mind narrows to protection and control.",
      painTruth: "Pain doesn't just come from injury or circumstance. It often comes from how tightly we're holding ourselves while living our lives."
    },
    expansionPractice: {
      title: "Expansion Isn't Something We Force",
      icon: "logo",
      intro: "Expansion isn't about becoming better, higher, or more evolved. It's about removing the inner pressure.",
      showsUpWhen: [
        "We soften our grip on self-judgment",
        "We stop fighting our own humanity",
        "We feel safe enough to feel what's actually here",
        "We allow curiosity instead of condemnation"
      ],
      key: "Expansion doesn't require confidence. It requires friendliness."
    },
    simplestTruth: {
      title: "The Simplest Truth",
      icon: "logo",
      content: "If we like ourselves, life feels good. If we don't like ourselves, life does NOT feel good.",
      friendQuestion: "Most people would never speak to a friend the way they speak to themselves. Yet we wonder why we feel contracted, tired, anxious, or disconnected."
    },
    bestFriend: {
      title: "Are We Our Own Best Friend?",
      icon: "logo",
      intro: "Being your own best friend doesn't mean constant positivity. It means non-abandonment.",
      means: [
        "Staying present with yourself when things feel messy",
        "Not making yourself wrong for being human",
        "Listening instead of correcting",
        "Offering support before solutions"
      ],
      key: "Expansion happens when the inner environment becomes safe. Not perfect. Safe."
    },
    books: [
      { title: "Eastern Body, Western Mind", author: "Anodea Judith" },
      { title: "The Subtle Body", author: "Cyndi Dale" },
      { title: "Hands of Light", author: "Barbara Brennan" }
    ]
  },
  'body-is-a-pump': {
    id: 'body-is-a-pump',
    title: 'Did You Know Your Body Is a Pump?',
    subtitle: 'Understanding Your Living Pump System',
    image: 'https://customer-assets.emergentagent.com/job_hello-starter-17/artifacts/si94iuao_eb87f745-a947-4a79-8a2d-ec9ce943a614.png',
    downloadUrl: 'https://customer-assets.emergentagent.com/job_hello-starter-17/artifacts/4e3tcya5_Did%20You%20Know%20Your%20Body%20Is%20a%20Pump%20%282%29.pdf',
    introduction: "Your body is not just a collection of parts. It's a living, sensing pump system. Every breath, every shift of weight, every spinal ripple moves fluids, pressure, and information. When that movement is interrupted, the pumps quiet down. When the pumps quiet down, circulation, communication, and healing all diminish.",
    keyInsight: "We don't just have pumps. We are pumps.",
    sections: [
      {
        title: "More Than Mechanical: Hydraulic, Electrical, Emotional",
        icon: "logo",
        content: "The heart is only one pump. The body relies on a layered system. These systems are inseparable. You cannot change one without influencing the others.",
        items: [
          { term: "Hydraulic pumps", definition: "Moving blood, lymph, cerebrospinal fluid, and digestive flow" },
          { term: "Mechanical pumps", definition: "Created by muscles, joints, and pressure changes" },
          { term: "Electrical signaling", definition: "Through fascia and connective tissue" },
          { term: "Emotional and mental tone", definition: "Shaping how freely all of the above can move" }
        ]
      },
      {
        title: "The Spinal Pump Effect",
        icon: "logo",
        content: "Imagine this... with each breath you take: The lungs expand the ribs. The ribs are connected to the spine. The spine expands with each inhale and retracts on each exhale, like an accordion. This spinal pump effect allows space for each nerve exiting the vertebral spaces. This nerve space allows impulses to travel more fully and with less constriction—like a light shining at half brightness vs full brightness. You have THAT capability in your own body with each breath!"
      },
      {
        title: "Emotional and Mental Tightness: Invisible Brakes on the Pump",
        icon: "logo",
        content: "The body doesn't separate physical tension from emotional or mental holding. Stress, vigilance, unresolved emotion, and prolonged mental strain create tone in the tissues. That tone becomes physical compression.",
        items: [
          { term: "A held breath", definition: "Limits diaphragm pump action" },
          { term: "A guarded belly", definition: "Restricts organ glide" },
          { term: "A locked jaw or neck", definition: "Alters nervous system signaling" },
          { term: "A braced pelvis", definition: "Dampens pelvic floor rebound" }
        ]
      },
      {
        title: "Energy Pathways and Meridian Flow",
        icon: "logo",
        content: "Alongside fluid and electrical systems, the body also operates through energetic pathways often described as meridians. These pathways rely on openness, elasticity, and continuity. Compression, scar tissue, postural collapse, or emotional bracing can disrupt flow along these lines just as surely as a kink in a hose.",
        items: [
          { term: "Sensation dulls", definition: "When energy flow is reduced" },
          { term: "Organs lose responsiveness", definition: "Communication decreases" },
          { term: "Recovery slows", definition: "Healing capacity diminishes" },
          { term: "Nervous system stays on alert", definition: "Unable to rest and restore" }
        ]
      },
      {
        title: "Fascia: Where Pumps, Signals, and Energy Meet",
        icon: "logo",
        content: "Fascia is not passive wrapping. It is a responsive, sensing network. Movement creates charge. Compression and release generate information. When fascia is dehydrated or immobile, both fluid flow and signaling degrade. This is why subtle, intentional movement can feel profound. You're not forcing change. You're reintroducing information.",
        items: [
          { term: "Transmits force", definition: "Throughout the entire body" },
          { term: "Conducts electrical signals", definition: "Communication network" },
          { term: "Responds to pressure", definition: "Adapts to load and movement" },
          { term: "Adapts to emotional tone", definition: "Holds our experiences" }
        ]
      },
      {
        title: "Organ Mobility: Reawakening a Forgotten Pump System",
        icon: "logo",
        content: "Organs are designed to move, glide, and respond to gravity. When organs lose mobility, local pumps fail. That affects digestion, detoxification, immune response, hormonal signaling, and nervous system balance. Organ reset work restores permission to move, not by force, but by reestablishing space, alignment, and rhythm.",
        items: [
          { term: "Each organ has its own rhythm", definition: "A natural pulsation" },
          { term: "Each organ has its own suspension", definition: "How it hangs in the body" },
          { term: "Each organ has its own relationship", definition: "To breath and posture" }
        ]
      }
    ],
    breathExercise: {
      title: "Try This Right Now",
      icon: "logo",
      intro: "Take a breath. How does it feel?",
      steps: [
        "Take a breath imagining your spine like an accordion. What do you notice?",
        "Relax your shoulders and jaw... take an inhale like you mean it. Notice your body open? Get taller?",
        "Exhale... like you mean it. See how your body folds on itself? Gets shorter?",
        "Now relax your eyes... breathe fully all the way into the top of your head. Did anything shift?",
        "Inhale with your whole entire body—full expansion, growing tall, lifting high. Exhale with your full body."
      ],
      insight: "You weren't delivering your entire light source. Now you are."
    },
    reawakening: {
      title: "Reawakening the Whole System",
      icon: "logo",
      content: "Healing doesn't require pushing harder. It requires restoring organization. When tubes align, bags suspend, tissues soften, and motion returns, the pumps wake up. Fluids move. Signals sharpen. Energy circulates.",
      keyInsight: "The body remembers itself."
    },
    closingThought: "Your body isn't broken. It may simply be compressed, guarded, and waiting for movement to return.\n\nOrgan Reset and fascial work is so important. You don't NEED a gym, although that can be supportive. If you are not well or vibrant and really don't know where to start... start with The Organ Reset, Walking, moving your arms around, swings, hug knees into chest, massage can help.\n\nPlease allow yourself to open your mind to the many possibilities that are accessible."
  },
  'intro-to-metaphysics': {
    id: 'intro-to-metaphysics',
    title: 'Introduction To Metaphysics',
    subtitle: 'When the Body Speaks, Listen',
    image: 'https://customer-assets-v7afamib.emergentagent.net/job_9cc27ad2-9473-4d8d-951e-838ca155566b/artifacts/xwq5781e_original-9349871-1.jpg',
    introduction: "Most of us prefer to think of the body as just a body — a collection of bones, muscles, and organs that sometimes cooperate and sometimes don't. When pain shows up, we usually look for the nearest physical explanation: too much stress, a strained muscle, a hormone out of balance. Rarely do we stop to ask: what else might this be telling me?",
    bodyNeverLies: {
      title: "The Body Never Lies",
      icon: "logo",
      paragraphs: [
        "The truth is, our bodies are not only physical — they are plasma-and-particle vessels, alive with energy, constantly transmitting signals from deeper layers of who we are. If the mind is quick to spin stories, hide truths, or distract us with endless chatter, the body is refreshingly direct. It never lies.",
        "Much of what the body reveals is rooted in the subconscious — or what some would call the unconscious — the unseen layers where emotions, memories, and stress patterns quietly live. We may not always be aware of what we've tucked away, but the body is. It faithfully translates those hidden experiences into tangible form: tight shoulders, a heavy chest, a restless gut, or even long-term illness."
      ],
      insight: "And when you start to look for it, you realize… we already know this. We've just never been taught to trust it."
    },
    bodySignals: {
      title: "The Body Speaking in Real Time",
      icon: "logo",
      signals: [
        { signal: "Your stomach drops", meaning: "before bad news lands" },
        { signal: "Your chest tightens", meaning: "when you're anxious or overwhelmed" },
        { signal: "Your shoulders creep up", meaning: "when you're carrying too much" },
        { signal: "Your jaw clenches", meaning: "when something feels unfair or unspoken" },
        { signal: "Your throat tightens", meaning: "when you're holding back words" },
        { signal: "You feel butterflies", meaning: "when you're excited or stepping into something new" },
        { signal: "You feel physically heavy", meaning: "during grief" },
        { signal: "You feel energized and lighter", meaning: "when something feels aligned" }
      ],
      insight: "These aren't random reactions — they are the body speaking in real time."
    },
    symptomsAsMessages: {
      title: "Symptoms as Messages",
      icon: "logo",
      content: "From a metaphysical perspective, symptoms are not random flaws or punishments. They are messages — gentle (and sometimes not-so-gentle) invitations to listen more deeply.",
      questions: [
        "What is unspoken?",
        "What is unprocessed?",
        "What part of me needs to be heard or released?"
      ],
      note: "This approach does not replace medical care. Instead, it adds another layer of wisdom. It invites us to see the body not as something that betrays us, but as something that communicates with us — consistently, honestly, and often quite brilliantly.",
      insight: "So when the body speaks — through sensation, tension, or illness — perhaps the most powerful thing we can do… is listen."
    },
    bodyMap: {
      title: "Common Body Signals & What They May Reflect",
      icon: "logo",
      intro: "As you begin to tune into your body, you may notice patterns in where you feel things. These are not rules, but gentle reference points — places to get curious rather than certain.",
      areas: [
        { area: "Head", meaning: "Mental overload, overthinking, pressure, trying to control outcomes" },
        { area: "Jaw", meaning: "Suppressed frustration, unspoken words, holding tension" },
        { area: "Neck", meaning: "Resistance, difficulty seeing another perspective, stubbornness" },
        { area: "Shoulders", meaning: "Carrying responsibility, burdens, 'holding it all together'" },
        { area: "Chest / Heart Space", meaning: "Grief, love, emotional openness or protection" },
        { area: "Upper Back", meaning: "Feeling unsupported, unseen, or emotionally alone" },
        { area: "Lower Back", meaning: "Security concerns (money, safety, stability)" },
        { area: "Stomach / Gut", meaning: "Processing emotions, anxiety, 'gut instincts,' worry" },
        { area: "Solar Plexus", meaning: "Personal power, control, self-worth" },
        { area: "Hips", meaning: "Stored emotions, fear of moving forward, old emotional residue" },
        { area: "Pelvis / Reproductive", meaning: "Creativity, relationships, intimacy, identity" },
        { area: "Hands", meaning: "Control, giving/receiving, holding on vs letting go" },
        { area: "Legs", meaning: "Forward movement, direction in life, independence" },
        { area: "Knees", meaning: "Flexibility, ego, ability to bend and adapt" },
        { area: "Feet", meaning: "Grounding, stability, trust in your path" }
      ]
    },
    closingThought: "You don't need to analyze every sensation. Sometimes simply noticing — 'oh, my shoulders are tight today' — is enough to begin shifting the pattern. Awareness softens what force cannot."
  }
};
